import type L from 'leaflet';
import { useMapEvents } from 'react-leaflet';

/**
 * How long after a gesture stops before the pin's coordinates are committed
 * (and reverse-geocoded). Short enough to feel instant, long enough that a
 * flick-pan-flick sequence is a single lookup.
 */
export const PIN_SETTLE_MS = 350;

type Props = {
  onMoveStart: () => void;
  onMoveEnd: (map: L.Map) => void;
};

/**
 * Bridges Leaflet's pan/zoom gestures to React for the centre-pin address
 * screens.
 *
 * Two rules this exists to enforce, both of which the inline copies in
 * AddingAddress/EditAddress got wrong:
 *
 * 1. It lives at module scope. A component type declared inside a render body
 *    is a new type on every render, so React unmounts and remounts it — which
 *    re-subscribed these Leaflet listeners dozens of times per drag.
 * 2. It listens to `moveend`/`zoomend`, not `move`/`zoom`. The continuous
 *    events fire on every animation frame of a gesture; running a React state
 *    update from each one is what made dragging stutter.
 *
 * The caller must not feed the resulting coordinates back into a prop that
 * re-centres the map: setView() emits `move`, which lands right back here.
 * Recentre imperatively through a map ref instead.
 */
export const MapMoveHandler = ({ onMoveStart, onMoveEnd }: Props) => {
  const map = useMapEvents({
    movestart: () => onMoveStart(),
    moveend: () => onMoveEnd(map),
    zoomend: () => onMoveEnd(map),
  });

  return null;
};
